import { useCallback } from 'react'

import { EMPTY_STRING } from '../../../../../constants/common'
import { stopPropagation } from '../../../../../utils/events'
import { COLOR_FORMAT_HEX } from '../../../ColorPicker.constants'
import { isColorFormatRgb } from '../../../ColorPicker.utils'
import { FIELD_ACTIVE_VALUE_KEY } from '../../ColorPickerField.constants'
import {
  formatRgbInput,
  getDisplayColorValue,
  normalizeHexColor,
  parseColorByFormat,
  parseStrictHexManualInput,
} from '../../ColorPickerField.utils'

export function useColorPickerFieldHandlers({
  committedColor,
  disabled,
  draftPopoverColor,
  draftValueState,
  format,
  handleClosePopover,
  refs,
  setDraftColor,
  setDraftFormat,
  setDraftPopoverColor,
  setDraftValueState,
  setFormat,
  onBlur,
  onChange,
  onClear,
}) {
  const handleCancelPopover = useCallback(() => {
    setDraftPopoverColor(committedColor)
    setDraftFormat(format)
    setDraftValueState({
      color: committedColor,
      format,
      value: getDisplayColorValue(committedColor, format),
    })
    handleClosePopover()
  }, [
    committedColor,
    format,
    handleClosePopover,
    setDraftFormat,
    setDraftPopoverColor,
    setDraftValueState,
  ])

  const handleSavePopover = useCallback(() => {
    const trimmed = (draftPopoverColor || EMPTY_STRING).trim()
    const next = trimmed ? normalizeHexColor(draftPopoverColor) : EMPTY_STRING

    onChange(next)
    setFormat(COLOR_FORMAT_HEX)
    handleClosePopover()
  }, [draftPopoverColor, handleClosePopover, onChange, setFormat])

  const handlePopoverChange = useCallback(
    nextValue => {
      setDraftColor(nextValue[FIELD_ACTIVE_VALUE_KEY].color)
    },
    [setDraftColor]
  )

  const handleInputClear = useCallback(
    event => {
      if (!disabled) {
        stopPropagation(event)

        setDraftPopoverColor(EMPTY_STRING)
        setDraftValueState({
          color: EMPTY_STRING,
          format,
          value: EMPTY_STRING,
        })
        onClear?.()
      }
    },
    [disabled, format, onClear, setDraftPopoverColor, setDraftValueState]
  )

  function handleInputChange(event) {
    if (!disabled) {
      const nextDraftValue = event.target.value

      setDraftValueState({
        color: draftPopoverColor,
        format,
        value: nextDraftValue,
      })

      if (!isColorFormatRgb(format)) {
        const parsedColor = parseStrictHexManualInput(nextDraftValue)

        if (parsedColor) {
          const normalizedColor = normalizeHexColor(parsedColor)

          setDraftPopoverColor(normalizedColor)
          setDraftValueState({
            color: normalizedColor,
            format,
            value: getDisplayColorValue(normalizedColor, format),
          })
        }
      }
    }
  }

  function handleInputBlur(event) {
    const related = event.relatedTarget
    const focusStaysInFloating =
      related != null && refs.floating?.current?.contains(related)

    if (!disabled && !focusStaysInFloating) {
      const fieldValue = event?.target?.value ?? draftValueState.value
      const trimmedDraft = fieldValue.trim()

      if (!trimmedDraft) {
        setDraftPopoverColor(EMPTY_STRING)
        setDraftValueState({
          color: EMPTY_STRING,
          format,
          value: EMPTY_STRING,
        })

        if (committedColor) {
          onChange(EMPTY_STRING)
        }
      } else {
        const parsedColor = parseColorByFormat(fieldValue, format)

        if (parsedColor) {
          const normalizedColor = normalizeHexColor(parsedColor)

          setDraftPopoverColor(normalizedColor)
          setDraftValueState({
            color: normalizedColor,
            format,
            value: isColorFormatRgb(format)
              ? formatRgbInput(fieldValue)
              : getDisplayColorValue(normalizedColor, format),
          })

          if (normalizedColor !== committedColor) {
            onChange(normalizedColor)
          }
        } else {
          setDraftValueState({
            color: committedColor,
            format,
            value: getDisplayColorValue(committedColor, format),
          })
        }
      }
    }

    onBlur?.()
  }

  return {
    handleCancelPopover,
    handleInputBlur,
    handleInputChange,
    handleInputClear,
    handlePopoverChange,
    handleSavePopover,
  }
}
